import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { adminApi } from "../../services/api";
import { showToast } from "../../store/uiSlice";

const StaffInbox = () => {
  const dispatch = useDispatch();
  const { adminUser } = useSelector((state) => state.admin);
  
  const [emailSenderOptions, setEmailSenderOptions] = useState([]);
  const [inboxEmails, setInboxEmails] = useState([]);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [selectedEmailBody, setSelectedEmailBody] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [bodyLoading, setBodyLoading] = useState(false);

  useEffect(() => {
    fetchEmailCredentials();
  }, []);

  const fetchEmailCredentials = async () => {
    try {
      const res = await adminApi.get("/admin/email/credentials");
      const options = res.data?.emailOptions || [];
      setEmailSenderOptions(options);
      if (options.length > 0) {
        setSelectedAccount(options[0].email);
        loadInbox(options[0].email);
      }
    } catch (e) {
      dispatch(showToast({ message: "Failed to load email accounts", type: "error" }));
    }
  };

  const loadInbox = async (email) => {
    if (!email) return;
    setInboxLoading(true);
    setSelectedEmailBody(null);
    try {
      // 1. Fetch raw emails from IMAP
      const res = await adminApi.get(`/admin/email/inbox?email=${email}`);
      const rawEmails = res.data?.emails || [];

      // 2. Check every email against the leads in parallel
      const enriched = await Promise.all(rawEmails.map(async (em) => {
        try {
          const lookup = await adminApi.post("/leads/lookup-email", { email: em.from });
          return { ...em, lead: lookup.data.found ? lookup.data.lead : null };
        } catch (e) {
          return { ...em, lead: null };
        }
      }));

      // 3. ONLY show emails that belong to the staff member's assigned leads
      const myLeadsOnly = enriched.filter(em => em.lead !== null);
      setInboxEmails(myLeadsOnly);

    } catch (e) {
      dispatch(showToast({ message: "Failed to load inbox", type: "error" }));
    } finally {
      setInboxLoading(false);
    }
  };

  const handleViewEmailBody = async (uid) => {
    setBodyLoading(true);
    setSelectedEmailBody({ subject: "Loading...", from: "", html: "<p>Loading email body...</p>" });
    try {
      const res = await adminApi.get(`/admin/email/body/${uid}?email=${selectedAccount}`);
      setSelectedEmailBody(res.data);
    } catch (e) {
      dispatch(showToast({ message: "Failed to load email body", type: "error" }));
      setSelectedEmailBody(null);
    } finally {
      setBodyLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-80px)]">
      <div className="flex flex-wrap items-center justify-between gap-4 p-5 border-b-2 border-[#e2e8f0] bg-[#f8fafc] flex-shrink-0">
        <div>
          <h2 className="font-syne text-xl font-extrabold text-[#0b1e33] uppercase tracking-tight">STAFF INBOX</h2>
          <p className="text-[11px] text-[#475569] font-mono font-extrabold mt-1 uppercase tracking-wider">Replies from your assigned leads</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider">Account:</label>
          <select 
            value={selectedAccount} 
            onChange={(e) => { setSelectedAccount(e.target.value); loadInbox(e.target.value); }}
            className="bg-white border-2 border-[#cbd5e1] rounded-xl px-3 py-2 text-[#0f172a] text-[10px] font-extrabold outline-none focus:border-[#1e3a5f] cursor-pointer"
          >
            {emailSenderOptions.map((opt, i) => (
              <option key={i} value={opt.email}>{opt.name}</option>
            ))}
          </select>
          <button onClick={() => loadInbox(selectedAccount)} className="px-4 py-2 rounded-xl bg-[#1e3a5f] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[2px] border-[#0b1e33]">
            <i className={`fas fa-sync-alt mr-1.5 ${inboxLoading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* List */}
        <div className="w-1/3 border-r-2 border-[#e2e8f0] overflow-y-auto bg-white">
          {inboxLoading ? (
            <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-[#7c3aed] border-t-transparent rounded-full animate-spin" /></div>
          ) : inboxEmails.length === 0 ? (
            <div className="text-center py-12 text-[#64748b]">
              <i className="fas fa-envelope-open text-3xl mb-3 opacity-30" />
              <p className="font-syne font-extrabold uppercase tracking-wider text-xs">No emails</p>
              <p className="text-[10px] font-mono mt-1">Replies from your leads will appear here.</p>
            </div>
          ) : (
            inboxEmails.map((em) => (
              <div key={em.uid} onClick={() => handleViewEmailBody(em.uid)} className={`p-4 border-b border-[#f1f5f9] cursor-pointer hover:bg-[#f8fafc] transition-all ${selectedEmailBody?.subject === em.subject ? 'bg-[#f0fdfa] border-l-4 border-l-[#0d9488]' : ''}`}>
                {/* MASKED: Show lead name instead of email */}
                <div className="font-syne text-sm font-extrabold text-[#0f172a] truncate">
                  {em.lead?.name}
                  {em.lead?.short_id && <span className="ml-2 text-[9px] text-[#1e3a5f] bg-[#eff6ff] px-1.5 py-0.5 rounded border border-[#bfdbfe]">#{em.lead.short_id}</span>}
                </div>
                <div className="text-[10px] text-[#64748b] font-mono truncate">{em.subject}</div>
                <div className="text-[9px] text-[#94a3b8] font-mono mt-1">{new Date(em.date).toLocaleDateString()}</div>
              </div>
            ))
          )}
        </div>
        
        {/* Body */}
        <div className="w-2/3 p-6 overflow-y-auto bg-[#f8fafc]">
          {bodyLoading ? (
            <div className="flex justify-center items-center h-full">
              <div className="w-8 h-8 border-[3px] border-[#1e3a5f] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : selectedEmailBody ? (
            <div className="bg-white p-6 rounded-xl border-2 border-[#e2e8f0] shadow-sm">
              <h4 className="font-syne text-lg font-extrabold text-[#0f172a] mb-2">{selectedEmailBody.subject}</h4>
              {/* MASKED: From field shows the lead's name */}
              <p className="text-xs text-[#475569] font-bold mb-4 border-b border-[#e2e8f0] pb-2">
                From: {inboxEmails.find(e => e.subject === selectedEmailBody.subject)?.lead?.name || "Unknown Lead"}
              </p>
              <div className="text-sm text-[#0f172a]" dangerouslySetInnerHTML={{ __html: selectedEmailBody.html }} />
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-[#94a3b8] font-mono text-sm">
              <div className="text-center">
                <i className="fas fa-envelope-open-text text-4xl mb-3 opacity-20" />
                <p>Select an email to view</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffInbox;